// Embassy tab: stored Influence (03 §4.1) and the scouts that carried it away from here.
import type { FullSystem, Scout } from '@stars-and-ships/api-types';
import { Link } from 'react-router';
import { formatDuration, formatNumber } from '../../labels';
import { Panel } from '../ui';

/** Influence grows like resources between responses; the view animates it the same way. */
export function liveInfluence(system: FullSystem, now: number): number {
  const { stored, perHour, cap } = system.influence;
  const elapsed = Math.max(0, now - Date.parse(system.resources.evaluatedAt));
  return stored >= cap
    ? stored
    : Math.min(cap, stored + Math.floor((perHour * elapsed) / 3_600_000));
}

const SCOUTS_SHOWN = 10;

function scoutStatus(scout: Scout, now: number): string {
  if (scout.status === 'succeeded') return 'Report ready';
  if (scout.status === 'failed') return 'Failed';
  const left = Date.parse(scout.arrivesAt) - now;
  return left > 0 ? `arrives in ${formatDuration(left)}` : 'arriving…';
}

export function EmbassyTab({
  galaxyId,
  system,
  now,
  scouts,
}: {
  galaxyId: string;
  system: FullSystem;
  now: number;
  scouts: Scout[] | undefined;
}) {
  const { perHour, cap } = system.influence;
  const sent = (scouts ?? [])
    .filter((s) => s.originSystemId === system.id)
    .sort((a, b) => Date.parse(b.sentAt) - Date.parse(a.sentAt))
    .slice(0, SCOUTS_SHOWN);
  return (
    <div className="grid gap-4 2xl:grid-cols-2 2xl:items-start">
      <Panel title="Influence">
        <div data-influence className="flex justify-between gap-2 text-sm">
          <span className="text-muted">
            Stored <span className="text-xs text-faint">+{formatNumber(perHour)}/h</span>
          </span>
          <span className="font-mono tabular-nums text-text">
            {formatNumber(liveInfluence(system, now))} / {formatNumber(cap)}
          </span>
        </div>
        <p className="mt-2 text-xs text-faint">
          Scouts sent from here carry Influence. Enemy scouts must carry more than is stored here.
        </p>
      </Panel>
      <Panel title="Scouts sent from here">
        {sent.length === 0 ? (
          <p className="text-sm text-muted">
            No scouts sent from here yet. Scout a system from its screen.
          </p>
        ) : (
          <ul className="space-y-1 text-sm" data-embassy-scouts>
            {sent.map((s) => (
              <li key={s.id} className="flex justify-between gap-2">
                <Link to={`/g/${galaxyId}/systems/${s.targetSystemId}`} className="text-info">
                  {s.targetSystemName}
                </Link>
                <span
                  className={`font-mono text-xs tabular-nums ${s.status === 'failed' ? 'text-danger' : 'text-muted'}`}
                >
                  {scoutStatus(s, now)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
